import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { FormControlLabel, Radio, RadioGroup } from "@mui/material";
import basketCircle from "assets/img/svg/basketCircle";
import ModalBox from "components/Modal";
import MessageBlock from "components/shared/MessageBlock";
import StyledButton from "components/shared/StyledButton";
import StyledTextField from "components/shared/StyledTextField/StyledTextField";
import { isPeriodClosed } from "helpers/utils";
import type { Dispatch, SetStateAction } from "react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { IAdvanceSlice } from "store/advancePaymentSlice";
import type { IBeneficiaryPayment } from "store/paymentSlice";
import { useAppSelector } from "store/store";
import { PaymentStatus } from "store/types";
import { EPaymentStatus } from "types";

import styles from "./style.module.scss";

interface IDeleteAdvanceDetails {
    sliceData: IAdvanceSlice | null;
    resourceData: IBeneficiaryPayment | undefined;
    open: boolean;
    onClose: () => void;
    /**
     * Function to trigger when they confirm deletion.
     */
    onDelete: () => Promise<void>;
    deleteWholeAdvance: number | null;
    setDeleteWholeAdvance: Dispatch<SetStateAction<number | null>>;
}

/**
 * Delete modal for advance payment with check on payment status.
 */
const DeleteAdvanceDetails = ({
    sliceData,
    resourceData,
    open,
    onClose,
    onDelete,
    deleteWholeAdvance,
    setDeleteWholeAdvance,
}: IDeleteAdvanceDetails) => {
    const [t] = useTranslation();
    const [isLoading, setIsLoading] = useState(false);

    const { advanceSlices } = useAppSelector((state) => state.advancePayment);

    const getLinkedSlices = (): IAdvanceSlice[] => {
        if (!sliceData) return [];
        return advanceSlices.filter((el) => el.termId === sliceData.termId && el.paymentId === sliceData.paymentId);
    };

    const allLinkedSlices = getLinkedSlices();

    const sliceIndex = allLinkedSlices.findIndex((el) => el.id === sliceData?.id);

    const handleDelete = async () => {
        setIsLoading(true);
        await onDelete();
        setDeleteWholeAdvance(null);
        setIsLoading(false);
        onClose();
    };

    /**
     * Returns extra info / action depending on the status of the slice.
     */
    const getSlicePaymentStatus = () => {
        if (!resourceData || resourceData.deletedAt !== null) return <></>;
        let descriptionElement: JSX.Element;
        switch (resourceData.status) {
            case PaymentStatus.PAID:
                descriptionElement = (
                    <span className={styles["advance-options-info"]}>{t("AdvancePayment.DeleteModal.Paid")}</span>
                );
                break;
            case PaymentStatus.TO_PAY:
                descriptionElement = (
                    <span className={styles["advance-options-info"]}>{t("AdvancePayment.DeleteModal.ToPay")}</span>
                );
                break;
            case PaymentStatus.NOT_PAID:
                descriptionElement = <></>;
        }

        return descriptionElement;
    };

    const disableButton = useMemo(() => {
        let loading = false;
        if (isLoading) {
            loading = true;
        } else if (allLinkedSlices.length > 1 && deleteWholeAdvance === null) {
            loading = true;
        }
        return loading;
    }, [isLoading, deleteWholeAdvance, allLinkedSlices]);

    useEffect(() => {
        if (allLinkedSlices.length === 1) {
            setDeleteWholeAdvance(1);
        }
    }, [allLinkedSlices]);

    const handleClosingModal = () => {
        onClose();
        setDeleteWholeAdvance(null);
    };

    return (
        <ModalBox open={open} onClose={handleClosingModal} Icon={basketCircle}>
            <div className={styles["delete-modal-wrapper"]}>
                <div className={styles["delete-title"]}>
                    <h3>{t("AdvancePayment.DeleteModal.DeleteWholeAdvanceQuestion")}</h3>

                    {sliceData?.connectedTPD && (
                        <MessageBlock
                            type="warning"
                            text={t("DeleteModal.AlertMessage")}
                            small
                            style={{ marginTop: "10px" }}
                        />
                    )}
                </div>
                {allLinkedSlices.length > 1 ? (
                    <div className={styles["advance-options"]}>
                        <span>{t("AdvancePayment.DeleteModal.AdvanceQuestion", { index: sliceIndex + 1 })}</span>
                        <RadioGroup
                            row
                            value={deleteWholeAdvance}
                            onChange={(e) => setDeleteWholeAdvance(+e.target.value)}
                        >
                            <FormControlLabel
                                value={1}
                                control={<Radio checkedIcon={<CheckCircleIcon />} />}
                                label={t("DeleteModal.Yes")}
                            />
                            <FormControlLabel
                                value={0}
                                control={<Radio checkedIcon={<CheckCircleIcon />} />}
                                label={t("DeleteModal.No")}
                            />
                        </RadioGroup>
                        {deleteWholeAdvance !== null ? (
                            deleteWholeAdvance ? (
                                <span className={styles["advance-options-info"]}>
                                    {t("AdvancePayment.DeleteModal.FullPayment")}
                                </span>
                            ) : (
                                getSlicePaymentStatus()
                            )
                        ) : (
                            <></>
                        )}
                    </div>
                ) : (
                    <div className={styles["advance-options"]}>{getSlicePaymentStatus()}</div>
                )}
                <div className={styles["delete-buttons"]}>
                    <StyledButton onClick={() => onClose()} fullWidth buttonType="secondary">
                        {t("DeleteModal.Cancel")}
                    </StyledButton>
                    <StyledButton
                        onClick={handleDelete}
                        fullWidth
                        buttonType="danger"
                        loading={isLoading}
                        disabled={disableButton}
                    >
                        {!isLoading && t("DeleteModal.ConfirmDelete")}
                    </StyledButton>
                </div>
            </div>
        </ModalBox>
    );
};

export default DeleteAdvanceDetails;
